import React from 'react'

// What a model's words may do on screen. Its text can carry words someone
// else wrote (a client's note, a review, a message to the front desk), so it
// never gets to fetch anything on its own or send someone somewhere we don't
// know. Kept free of react-markdown so it can be tested on its own.

// A page in Beu ("/marketing-social"), not "//elsewhere.com"
export const inApp = (href) => typeof href === 'string' && href.charAt(0) === '/' && href.charAt(1) !== '/'

// Beu's own addresses, on every environment (app.beuapp.com, stag.beublog.com…)
const BEU_HOSTS = ['beuapp.com', 'beublog.com']

const hereHost = () => {
  try { return (typeof window !== 'undefined' && window.location && window.location.host) || '' } catch (e) { return '' }
}

const parse = (href) => {
  try { return new URL(href) } catch (e) { return null }
}

// A full address on this app (the same place the owner is): opened in place
// as an in-app page. Anything else returns null.
export const pathOnThisApp = (href) => {
  if (typeof href !== 'string' || inApp(href)) return null
  const u = parse(href)
  const here = hereHost()
  if (!u || !here || u.host !== here || (u.protocol !== 'https:' && u.protocol !== 'http:')) return null
  return `${u.pathname}${u.search}${u.hash}` || '/'
}

// An https address on one of Beu's own hosts
export const trustedHref = (href) => {
  if (typeof href !== 'string') return false
  if (inApp(href)) return true
  const u = parse(href)
  if (!u || u.protocol !== 'https:' || u.username || u.password) return false
  const host = u.hostname.toLowerCase()
  if (host === hereHost().split(':')[0]) return true
  return BEU_HOSTS.some(h => host === h || host.endsWith(`.${h}`))
}

const textOf = (children) => React.Children.toArray(children).map(c => (typeof c === 'string' || typeof c === 'number' ? String(c) : '')).join('')

// A link we won't open: its words stay, with where it pointed shown beside
// them as plain text, so nothing is hidden and nothing is one tap away.
export const PlainLink = ({ href, children }) => { // eslint-disable-line react/prop-types
  const words = textOf(children)
  const where = typeof href === 'string' ? href : ''
  return (
    <span className="bw-plain-link">
      {children}
      {where && where !== words ? ` (${where})` : null}
    </span>
  )
}

// Pictures in a reply are never loaded: an image fetches its address the
// moment it's drawn, with no tap, which would let words in the reply send
// what's around them anywhere. The description, if any, stays as words.
export const NoImage = ({ alt }) => (alt ? <span className="bw-no-img">{alt}</span> : null) // eslint-disable-line react/prop-types

// The link a reply draws. `go` opens a page in Beu in place; Beu's own
// addresses open in a new tab; anything else is shown, not linked.
export const linkFor = (go) => function BaileyLink({ href, children, node, ...rest }) { // eslint-disable-line react/prop-types
  const path = inApp(href) ? href : (go ? pathOnThisApp(href) : null)
  if (path && go) {
    return (
      <a
        {...rest}
        href={path}
        onClick={(e) => {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return
          e.preventDefault()
          go(path)
        }}
      >
        {children}
      </a>
    )
  }
  if (trustedHref(href)) return <a {...rest} href={href} target="_blank" rel="noopener noreferrer">{children}</a>
  return <PlainLink href={href}>{children}</PlainLink>
}

// The components map for any model-written markdown
export const safeComponents = (go = null) => ({ a: linkFor(go), img: NoImage })
